
import { useState } from "react";
import { Modal } from "../../ui/modal";
import { Banknote, CheckCircle, Copy } from "lucide-react";
import { Wallet, Transaction } from "../types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onPaymentComplete: (transaction?: Transaction) => void;
  wallet: Wallet;
  amount: number;
  fee: number;
}

export const BankTransferDetailsModal = ({
  isOpen,
  onClose,
  onPaymentComplete,
  wallet,
  amount,
}: Props) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Check if wallet has bank account details
  if (!wallet.hasBankAccount || !wallet.bankDetails) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:shadow-2xl"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <CheckCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
            Bank Account Not Found
          </h2>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            This wallet does not have a linked bank account. Please contact support.
          </p>
          <button
            onClick={onClose}
            className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Close
          </button>
        </div>
      </Modal>
    );
  }

  const bankDetails = wallet.bankDetails;
//   const amount = amount;

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setShowSuccess(true);
      
      // Reset success message after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        setCopiedField(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handlePaymentComplete = () => {
    setShowSuccess(true);
    setCopiedField(null);

      const pendingTransaction: Transaction = { 
    id: `local-${Date.now()}`,
    reference: `FUND-${Date.now()}`,
    amount,
    currency: wallet.currency,
    status: "PENDING",
    type: "CREDIT",
    purpose: "Fund Wallet",
    paymentMethod: "BANK_TRANSFER",
    createdAt: new Date().toISOString(),
  } as Transaction;
    
    setTimeout(() => {
      onPaymentComplete(pendingTransaction);
    }, 1000);
  };

  const renderCopyButton = (text: string, fieldName: string) => (
    <button
      onClick={() => handleCopy(text, fieldName)}
      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
      title={`Copy ${fieldName}`}
    >
      <Copy className="h-4 w-4" />
      {copiedField === fieldName && (
        <span className="text-xs text-green-600 dark:text-green-400">Copied!</span>
      )}
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:shadow-2xl"
    >
      {/* Success Message */}
      {showSuccess && copiedField === null && (
        <div className="mb-4 rounded-lg bg-green-50 p-3 text-center dark:bg-green-900/30">
          <p className="text-sm font-medium text-green-700 dark:text-green-300">
            Payment initiated! You'll be redirected shortly...
          </p>
        </div>
      )}

      <div className="mb-6 text-center">
        <Banknote className="mx-auto mb-4 h-12 w-12 text-green-500 dark:text-green-400" />
        <h4 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
          Please make a bank transfer to below details
        </h4>

      </div>

      {/* Amount Summary */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {wallet.symbol}{" "}
            {amount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </div>

      </div>

      {/* Bank Details */}
      <div className="mb-6 space-y-4">
        <div>
          <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Bank Name
          </label>
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600">
            <span className="font-medium text-gray-900 dark:text-white">
              {bankDetails.bankName}
            </span>
            {renderCopyButton(bankDetails.bankName, "Bank name")}
          </div>
        </div>

        <div>
          <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Account Number
          </label>
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600">
            <span className="font-medium text-gray-900 dark:text-white">
              {bankDetails.accountNumber}
            </span>
            {renderCopyButton(bankDetails.accountNumber, "Account number")}
          </div>
        </div>

        <div>
          <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Account Name
          </label>
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600">
            <span className="font-medium text-gray-900 dark:text-white">
              {bankDetails.accountName}
            </span>
            {renderCopyButton(bankDetails.accountName, "Account name")}
          </div>
        </div>

      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <button
          onClick={handlePaymentComplete}
          className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          disabled={showSuccess && copiedField === null}
        >
          {showSuccess && copiedField === null ? "Processing..." : "I've Made the Transfer"}
        </button>

      </div>
    </Modal>
  );
};